import React from 'react';
import { OneWayInOpportunitySimpleV2Dto } from 'src/api/v2';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ONE_WAY_IN_DETAILS } from '../../opportunity-details/routes';
import StatusDot from '../../opportunity-details/components/StatusDot/StatusDot';
import { hoverEnter, hoverLeave, dragEvent } from '../helpers';
import { Heading, Tooltip, Icon } from '@innovationnorway/cl-react';
import AdvisorControl from '../../opportunity-details/components/AdvisorDropdown/AdvisorControl';

// toLocaleDateString() constructs a new Intl.DateTimeFormat on every call, which is costly per card.
const dateFormatter = new Intl.DateTimeFormat();

const OpportunityCard = ({
  opportunity,
  municipalityName,
  advisorName,
  advisorInitials,
  advisorColorClass,
  advisorsLoading,
  onRecommendServicesModalOpen,
  onDeliverServicesModalOpen,
}: {
  opportunity: OneWayInOpportunitySimpleV2Dto;
  municipalityName: string | null | undefined;
  advisorName: string | undefined;
  advisorInitials: string | undefined;
  advisorColorClass: string;
  advisorsLoading: boolean;
  onRecommendServicesModalOpen: (opportunity: OneWayInOpportunitySimpleV2Dto) => void;
  onDeliverServicesModalOpen: (opportunity: OneWayInOpportunitySimpleV2Dto) => void;
}) => {
  const { t: tCommon } = useTranslation('common');
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  return (
    <div
      onDragEnter={(e) => {
        hoverEnter(e);
      }}
      onDragLeave={(e) => {
        hoverLeave(e);
      }}
      onDragStart={(e) => dragEvent(e, opportunity)}
      draggable
      className="board__card stack-medium"
      key={opportunity.id}
    >
      <Link
        to={ONE_WAY_IN_DETAILS.BASE(opportunity.oneWayInId)}
        draggable={false}
        className="stack-medium text-decoration__none"
      >
        <div className="flex gap-x--2 gap-y--1">
          {opportunity.organizationName && (
            <div className="icon">
              <Icon prefix="fasr" iconName="id-card-clip" />
              <div className="icon-content">{opportunity.customerName}</div>
            </div>
          )}
          <div className="icon">
            <Icon prefix="fasr" iconName={opportunity.organizationName ? 'building' : 'id-card-clip'} />

            <div className="icon-content">{opportunity.organizationName || opportunity.customerName}</div>
          </div>
          <div className="icon">
            <Icon prefix="fasr" iconName="map-marker-alt" />{' '}
            <div className="icon-content">{municipalityName}</div>
          </div>
        </div>
        <div className="display--flex gap--1 align-items--baseline">
          <StatusDot opportunity={opportunity} />
          <Heading level={2} className="heading">
            {opportunity.title}
          </Heading>
        </div>
        <div className="flex gap--1">
          <div className="stack-small">
            <div className="date">
              {opportunity.submittedOn && (
                <span className="text-decoration--none border-right padding-x--1">
                  {tCommon('delivered')}: {dateFormatter.format(new Date(opportunity.submittedOn))}
                </span>
              )}
            </div>
          </div>
          <div className="date">
            <span>
              {tCommon('lastChanged')}:{' '}
              {opportunity.lastChangedOn && dateFormatter.format(new Date(opportunity.lastChangedOn))}
            </span>
          </div>
        </div>
      </Link>
      <div className="display--flex align-items--center gap--2">
        <Tooltip content={tOneWayIn('button.RecommendService')}>
          <button
            className="action-button--container"
            onClick={() => onRecommendServicesModalOpen(opportunity)}
          >
            <Icon prefix="fasr" iconName="hand-holding-heart" />
          </button>
        </Tooltip>
        <Tooltip content={tOneWayIn('customerFollowUp.deliverService')}>
          <button
            className="action-button--container"
            onClick={() => onDeliverServicesModalOpen(opportunity)}
          >
            <Icon prefix="fasr" iconName="hands-holding-heart" />
          </button>
        </Tooltip>
        <Tooltip
          content={
            advisorName
              ? `${tOneWayIn('tooltip.changeAdvisor')} (${advisorName})`
              : tOneWayIn('tooltip.addAdvisor')
          }
        >
          <AdvisorControl
            opportunity={opportunity}
            initials={advisorInitials}
            colorClass={advisorColorClass}
            label={advisorName ? tOneWayIn('advisor.changeAdvisor') : tOneWayIn('advisor.assignAdvisor')}
            isLoading={advisorsLoading}
          />
        </Tooltip>
      </div>
    </div>
  );
};

export default React.memo(OpportunityCard);
